import { z } from 'zod';

import {
  OUTBOUND_ATTACHMENT_MAX_BYTES,
  OUTBOUND_ATTACHMENT_MAX_COUNT,
  OUTBOUND_CC_MAX_COUNT,
  OUTBOUND_SUBJECT_MAX_LENGTH,
  OUTBOUND_TO_MAX_COUNT,
} from '@minty/shared/email';
import type { TFunction } from 'i18next';
import type { Id } from '@minty/shared/ids';
import type { ComposerMode, ForwardAttachment } from './types';
import type { UploadedAttachment } from './upload-attachments';

export type ComposerFormValues = {
  to: Array<string>;
  cc: Array<string>;
  subject: string;
  attachments: Array<File>;
  // Forward mode: which of the original's attachments survived the chips.
  keptForwardAttachmentIds: Array<Id<'files'>>;
};

export type EmailBody = {
  html: string;
  text: string;
};

export type OutboundMode =
  | { kind: 'forward'; emailId: Id<'emails'>; to: Array<{ email: string }> }
  | { kind: 'new'; subject: string; to: Array<{ email: string }> }
  | { kind: 'reply'; emailId: Id<'emails'>; all: boolean };

const fileId = z.custom<Id<'files'>>((value) => typeof value === 'string');

// Reply leaves recipients/subject unvalidated: they are derived server-side
// and rendered read-only, so the client only submits which reply mode to use.
export function createComposerSchema(
  t: TFunction,
  mode: ComposerMode,
  forwardAttachments: Array<ForwardAttachment> = [],
) {
  const forwardSizes = new Map(
    forwardAttachments.map(({ size, storageId: id }) => [id, size ?? 0]),
  );

  return (
    z
      .object({
        to:
          mode !== 'reply'
            ? z
                .array(
                  z.email({
                    message: t('Enter a valid email address.', {
                      ns: 'mailbox',
                    }),
                  }),
                )
                .min(1, t('Add at least one recipient.', { ns: 'mailbox' }))
                .max(
                  OUTBOUND_TO_MAX_COUNT,
                  t('Too many recipients.', { ns: 'mailbox' }),
                )
            : z.array(z.string()),
        cc: (mode === 'reply'
          ? z.array(z.string())
          : z.array(
              z.email({
                message: t('Enter a valid email address.', { ns: 'mailbox' }),
              }),
            )
        ).max(
          OUTBOUND_CC_MAX_COUNT,
          t('Too many CC recipients.', { ns: 'mailbox' }),
        ),
        subject:
          mode === 'new'
            ? z
                .string()
                .trim()
                .min(1, t('Add a subject.', { ns: 'mailbox' }))
                .max(
                  OUTBOUND_SUBJECT_MAX_LENGTH,
                  t('Subject must be {{count}} characters or fewer.', {
                    count: OUTBOUND_SUBJECT_MAX_LENGTH,
                    ns: 'mailbox',
                  }),
                )
            : z.string(),
        keptForwardAttachmentIds: z.array(fileId),
        attachments: z.array(z.instanceof(File)).superRefine((files, ctx) => {
          if (files.some((file) => file.size === 0)) {
            ctx.addIssue({
              code: 'custom',
              message: t('Attachments cannot be empty.', { ns: 'mailbox' }),
            });
          }

          if (files.some((file) => file.name.trim().length === 0)) {
            ctx.addIssue({
              code: 'custom',
              message: t('Attachment names cannot be empty.', {
                ns: 'mailbox',
              }),
            });
          }

          if (files.some((file) => file.size > OUTBOUND_ATTACHMENT_MAX_BYTES)) {
            ctx.addIssue({
              code: 'custom',
              message: t('Each attachment must be {{size}} MB or smaller.', {
                size: OUTBOUND_ATTACHMENT_MAX_BYTES / 1024 / 1024,
                ns: 'mailbox',
              }),
            });
          }
        }),
      })
      // The budget spans kept forwards *and* new uploads, because the send does:
      // the chips are what let the user get back under it.
      .superRefine((values, ctx) => {
        const sizes = [
          ...values.keptForwardAttachmentIds.map(
            (id) => forwardSizes.get(id) ?? 0,
          ),
          ...values.attachments.map((file) => file.size),
        ];

        if (sizes.length > OUTBOUND_ATTACHMENT_MAX_COUNT) {
          ctx.addIssue({
            code: 'custom',
            path: ['attachments'],
            message: t('You can attach up to {{count}} files.', {
              count: OUTBOUND_ATTACHMENT_MAX_COUNT,
              ns: 'mailbox',
            }),
          });
        }

        if (
          sizes.reduce((total, size) => total + size, 0) >
          OUTBOUND_ATTACHMENT_MAX_BYTES
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['attachments'],
            message: t('Attachments must be {{size}} MB or smaller in total.', {
              size: OUTBOUND_ATTACHMENT_MAX_BYTES / 1024 / 1024,
              ns: 'mailbox',
            }),
          });
        }
      })
  );
}

/** The one mode object both outbound mutations take. */
export function toOutboundMode(
  values: ComposerFormValues,
  mode: ComposerMode,
  sourceEmailId: Id<'emails'> | null,
  replyAll: boolean,
): OutboundMode {
  if (mode === 'new') {
    return {
      kind: 'new',
      subject: values.subject.trim(),
      to: values.to.map((email) => ({ email })),
    };
  }

  if (!sourceEmailId) {
    throw new Error(`Original email is missing for ${mode}.`);
  }

  if (mode === 'reply') {
    return { all: replyAll, emailId: sourceEmailId, kind: 'reply' };
  }

  return {
    emailId: sourceEmailId,
    kind: 'forward',
    to: values.to.map((email) => ({ email })),
  };
}

export function toSendPayload({
  body,
  clientRequestId,
  mode,
  uploads,
  values,
}: {
  body: EmailBody;
  clientRequestId: string;
  mode: OutboundMode;
  uploads: Array<UploadedAttachment>;
  values: ComposerFormValues;
}) {
  const html = body.html.trim();

  return {
    /** Parallel by construction — `uploads` is what uploading these files
     *  produced. A gap would mean a nameless attachment, so it is dropped
     *  rather than sent. */
    attachments: uploads.flatMap(({ fileId, receipt }, index) => {
      const file = values.attachments[index];

      return file ? [{ filename: file.name, receipt, storageId: fileId }] : [];
    }),
    bcc: [],
    // Reply recipients are resolved server-side, so the client submits none.
    cc: mode.kind === 'reply' ? [] : values.cc.map((email) => ({ email })),
    clientRequestId,
    keptForwardAttachmentIds: values.keptForwardAttachmentIds,
    mode,
    text: body.text.trim(),
    ...(html ? { html } : {}),
  };
}
